/**
 * Business-email gate for the recruiter chat MVP.
 *
 * To SAVE/EXPORT a talent shortlist, a recruiter must sign up with a *business*
 * email — free webmail (gmail/yahoo/outlook/…) and disposable domains are
 * rejected, both as a lead-quality filter and to keep the pre-registered
 * success metric ("distinct business emails") meaningful.
 *
 * This is a heuristic domain denylist, not a verification service — it blocks
 * the long tail of consumer mailboxes without an external dependency. It is NOT
 * a deliverability check; the founder still reviews every lead.
 */

/** Consumer webmail + the common disposable-mail providers. Lowercase. */
const FREE_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
	// Major global webmail
	"gmail.com",
	"googlemail.com",
	"yahoo.com",
	"yahoo.co.id",
	"yahoo.co.uk",
	"ymail.com",
	"rocketmail.com",
	"outlook.com",
	"outlook.co.id",
	"hotmail.com",
	"hotmail.co.uk",
	"live.com",
	"msn.com",
	"icloud.com",
	"me.com",
	"mac.com",
	"aol.com",
	"protonmail.com",
	"proton.me",
	"pm.me",
	"gmx.com",
	"gmx.net",
	"mail.com",
	"zoho.com",
	"yandex.com",
	"yandex.ru",
	"tutanota.com",
	"fastmail.com",
	"hey.com",
	// Indonesia-local consumer/ISP webmail
	"telkom.net",
	"cbn.net.id",
	"indo.net.id",
	// Common disposable / throwaway providers
	"mailinator.com",
	"guerrillamail.com",
	"10minutemail.com",
	"tempmail.com",
	"temp-mail.org",
	"throwawaymail.com",
	"trashmail.com",
	"yopmail.com",
	"sharklasers.com",
	"getnada.com",
	"dispostable.com",
	"maildrop.cc",
]);

/**
 * Throwaway-mailbox providers. Subset of FREE_EMAIL_DOMAINS, exported so lead
 * scoring (src/lib/leads/score.ts) can tell "disposable" from plain webmail.
 */
export const DISPOSABLE_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
	"mailinator.com",
	"guerrillamail.com",
	"10minutemail.com",
	"tempmail.com",
	"temp-mail.org",
	"throwawaymail.com",
	"trashmail.com",
	"yopmail.com",
	"sharklasers.com",
	"getnada.com",
	"dispostable.com",
	"maildrop.cc",
]);

export type BusinessEmailRejection = "invalid" | "free_webmail" | "disposable";

export interface BusinessEmailCheck {
	ok: boolean;
	/** Lowercased, trimmed email when valid; otherwise the raw input. */
	email: string;
	/** Registrable domain (the part after @), lowercased. Empty when invalid. */
	domain: string;
	/** Reason the email was rejected, when `ok` is false. */
	reason?: BusinessEmailRejection;
}

/** Minimal RFC-ish email shape: one @, a dot in the domain, no spaces. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validate that an email belongs to a business domain. Pure + dependency-free
 * so it runs in any runtime and is trivially unit-testable.
 */
export function checkBusinessEmail(raw: string): BusinessEmailCheck {
	const email = (raw ?? "").trim().toLowerCase();
	if (!EMAIL_RE.test(email)) {
		return { ok: false, email: raw, domain: "", reason: "invalid" };
	}
	const domain = email.slice(email.lastIndexOf("@") + 1);
	if (FREE_EMAIL_DOMAINS.has(domain)) {
		// A disposable provider is a stricter rejection reason than a webmail one,
		// but both live in the same set; keep the user-facing message generic via
		// `free_webmail` unless the domain is a known throwaway.
		const reason: BusinessEmailRejection =
			DISPOSABLE_EMAIL_DOMAINS.has(domain) || domain.includes("temp")
				? "disposable"
				: "free_webmail";
		return { ok: false, email, domain, reason };
	}
	return { ok: true, email, domain };
}
